import { z } from 'zod';
import { MoneyNum, QtyNum } from './numbers';
import type { PermissionCode } from './permissions';
import { SplitView } from './procurement';

/* ───────────────────────── Договоры (6.8, Регламент № 33П) ───────────────────────── */

export const CONTRACT_KINDS = {
  supply: 'Поставка',
  works: 'Подряд',
  subcontract: 'Субподряд',
  services: 'Услуги',
  rent: 'Аренда техники',
  framework: 'Рамочный',
  revenue: 'Доходный, с заказчиком',
} as const;
export type ContractKind = keyof typeof CONTRACT_KINDS;
export const ContractKindSchema = z.enum(Object.keys(CONTRACT_KINDS) as [ContractKind, ...ContractKind[]]);

export const CONTRACT_PREFIX: Record<ContractKind, string> = {
  supply: 'П',
  works: 'ПД',
  subcontract: 'СП',
  services: 'У',
  rent: 'А',
  framework: 'Р',
  revenue: 'Д',
};

export const CONTRACT_STATUSES = {
  draft: 'Проект договора',
  approval: 'На согласовании',
  approved: 'Согласован',
  signed: 'Подписан',
  completed: 'Исполнен',
  terminated: 'Расторгнут',
} as const;
export type ContractStatus = keyof typeof CONTRACT_STATUSES;

/** Налоговый статус цены: без него доходный договор не сохраняется (ПФ-24) */
export const VAT_STATUSES = {
  with_vat: 'с учётом НДС',
  incl_vat: 'в том числе НДС',
  no_vat: 'без НДС',
} as const;
export type VatStatus = keyof typeof VAT_STATUSES;
export const VatStatusSchema = z.enum(Object.keys(VAT_STATUSES) as [VatStatus, ...VatStatus[]]);

/** Пять согласующих расходного договора по п. 6.1 Регламента № 33П; каждый — свою часть, 2 рабочих дня */
export const APPROVAL_STEPS = {
  initiator: {
    title: 'Руководитель проекта или инициатор',
    scope: 'предмет, объём, необходимость, сроки, соответствие ФП',
  },
  accounting: { title: 'Бухгалтерия', scope: 'налоговые условия, НДС, первичные документы, ЭСФ' },
  legal: { title: 'Юридическая служба', scope: 'правовая экспертиза' },
  deputy: { title: 'Заместитель директора', scope: 'условия в пределах полномочий' },
  security: { title: 'Служба безопасности', scope: 'контрагент и риски (раздел 6.12)' },
} as const;
export type ApprovalStep = keyof typeof APPROVAL_STEPS;
export const APPROVAL_DAYS = 2;

/**
 * Кто решает пункт маршрута (ДГ-04: «каждый согласует только свою часть»): инициатора — руководитель
 * проекта или тот, кто готовит договоры; остальные пункты — своё подразделение или «любой пункт маршрута»
 * для замещения.
 */
export function approvalStepPerms(step: ApprovalStep): PermissionCode[] {
  return step === 'initiator'
    ? ['contracts.edit', 'project.edit']
    : ['contracts.approve', `contracts.approve.${step}`];
}

export const PAYMENT_BASES = {
  advance: 'Аванс',
  on_delivery: 'По поставке',
  invoice_plus_days: 'Накладная + N дней',
  on_act: 'По акту',
  retention: 'Удержание',
} as const;
export type PaymentBasis = keyof typeof PAYMENT_BASES;

const Money = z.number().finite();
/** Ввод сумм — с пределом, чтобы ошибка ввода не превращалась в сбой сервера */
const MoneyIn = MoneyNum;
const DateStr = z.iso.date();

export const ContractItemInput = z.object({
  name: z.string().trim().min(2).max(500),
  unit: z.string().trim().min(1).max(30),
  quantity: QtyNum.positive(),
  price: MoneyIn.nonnegative(),
  requestLineId: z.uuid().nullable().optional(),
  budgetLineId: z.uuid().nullable().optional(),
});
export type ContractItemInput = z.infer<typeof ContractItemInput>;

export const ScheduleRowInput = z.object({
  /** Строка, которая уже есть: сохраняется, и заявки на оплату остаются связаны с ней (ФН-02) */
  id: z.uuid().optional(),
  kind: z.enum(['delivery', 'payment']),
  plannedDate: DateStr.nullable().optional(),
  basis: z
    .enum(Object.keys(PAYMENT_BASES) as [PaymentBasis, ...PaymentBasis[]])
    .nullable()
    .optional(),
  days: z.number().int().nonnegative().nullable().optional(),
  percent: z.number().min(0).max(100).nullable().optional(),
  amount: MoneyIn.nonnegative().nullable().optional(),
  quantity: QtyNum.nonnegative().nullable().optional(),
  /** Строка «поставка»: позиция спецификации — строка закрывается полученным по ней на склад (ДГ-09) */
  contractItemId: z.uuid().nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  done: z.boolean().optional(),
});
export type ScheduleRowInput = z.infer<typeof ScheduleRowInput>;

export const ContractInput = z.object({
  projectId: z.uuid(),
  counterpartyId: z.uuid(),
  kind: ContractKindSchema,
  number: z.string().trim().max(60).optional(),
  subject: z.string().trim().min(3).max(1000),
  amount: MoneyIn.nonnegative().optional(),
  currency: z.string().length(3).optional(),
  vatStatus: VatStatusSchema,
  vatRate: z.number().min(0).max(30).optional(),
  startDate: DateStr.nullable().optional(),
  endDate: DateStr.nullable().optional(),
  costArticle: z.string().max(200).nullable().optional(),
  paymentTerms: z.string().trim().max(1000).nullable().optional(),
  selectionReason: z.string().trim().max(2000).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  /** Договор требует гарантию возврата аванса: без неё аванс не оплачивается (ФН-19) */
  advanceGuarantee: z.boolean().optional(),
  /** Неустойка за просрочку поставки, % от стоимости непоставленного в день (ДГ-07) */
  penaltyRate: z.number().min(0).max(10).nullable().optional(),
  /** Предел неустойки, % от суммы договора */
  penaltyCap: z.number().min(0).max(100).nullable().optional(),
});
export type ContractInput = z.infer<typeof ContractInput>;

export const ContractSummary = z.object({
  id: z.uuid(),
  number: z.string(),
  kind: ContractKindSchema,
  direction: z.enum(['expense', 'revenue']),
  status: z.enum(Object.keys(CONTRACT_STATUSES) as [ContractStatus, ...ContractStatus[]]),
  subject: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  counterpartyId: z.uuid(),
  counterpartyName: z.string(),
  amount: Money.nullable(),
  currency: z.string(),
  vatStatus: VatStatusSchema,
  signedDate: DateStr.nullable(),
  endDate: DateStr.nullable(),
  signedBypass: z.boolean(),
  approvalsDone: z.number().int(),
  approvalsTotal: z.number().int(),
  approvalsOverdue: z.number().int(),
  requestId: z.uuid().nullable(),
  requestNumber: z.string().nullable(),
});
export type ContractSummary = z.infer<typeof ContractSummary>;

/** Причины допсоглашения (ДГ-08) */
export const AMENDMENT_REASONS = {
  psd_revision: 'Новая ревизия ПСД',
  extra_works: 'Дополнительные работы',
  price: 'Изменение цены',
  terms: 'Сроки и порядок оплаты',
  counterparty_change: 'Смена руководителя, участников или бенефициаров контрагента',
  other: 'Другое',
} as const;
export type AmendmentReason = keyof typeof AMENDMENT_REASONS;

export const AMENDMENT_STATUSES = {
  draft: 'Проект допсоглашения',
  approval: 'На согласовании',
  approved: 'Согласовано',
  signed: 'Подписано',
  cancelled: 'Отменено',
} as const;
export type AmendmentStatus = keyof typeof AMENDMENT_STATUSES;

/** Изменение суммы на 15 % и больше считается существенным: его проверяет служба безопасности (ДГ-08) */
export const AMENDMENT_SECURITY_PCT = 15;

export const AmendmentInput = z.object({
  reasonKind: z.enum(Object.keys(AMENDMENT_REASONS) as [AmendmentReason, ...AmendmentReason[]]),
  reason: z.string().trim().min(5).max(2000),
  newAmount: MoneyIn.positive().nullable().optional(),
  newEndDate: DateStr.nullable().optional(),
  newPaymentTerms: z.string().trim().max(2000).nullable().optional(),
  /** Новая статья ФП договора (в том числе если в договоре её не было); null — без изменений */
  newCostArticle: z.string().trim().min(1).max(200).nullable().optional(),
  scopeNote: z.string().trim().max(2000).nullable().optional(),
  securityCheck: z.boolean().optional(),
  /** Новая спецификация (объёмы и цены): сумма допсоглашения считается по ней; null — без изменений */
  items: z.array(ContractItemInput).max(500).nullable().optional(),
  /** Новые графики поставок и платежей; null — без изменений */
  schedule: z.array(ScheduleRowInput).max(200).nullable().optional(),
  /**
   * Запрос на изменение (ДГ-08, ИЗ-07): утверждённый, этого проекта и затрагивающий этот договор. После
   * подписания допсоглашение отмечается в запросе, а у доходного договора снимает риск работ без допсоглашения
   */
  changeId: z.uuid().nullable().optional(),
});
export type AmendmentInput = z.infer<typeof AmendmentInput>;

/** Что меняется: строка сравнения «было → стало» (ДГ-04, ДГ-08) */
export const ChangeLine = z.object({ label: z.string(), before: z.string(), after: z.string() });
export type ChangeLine = z.infer<typeof ChangeLine>;

const ApprovalView = z.object({
  id: z.uuid(),
  step: z.enum(Object.keys(APPROVAL_STEPS) as [ApprovalStep, ...ApprovalStep[]]),
  status: z.enum(['pending', 'approved', 'rejected', 'returned']),
  approverName: z.string().nullable(),
  /** Кто решил пункт: в раунде один человек решает один пункт (ДГ-04) */
  decidedById: z.uuid().nullable(),
  comment: z.string().nullable(),
  dueAt: DateStr.nullable(),
  decidedAt: z.string().nullable(),
  overdue: z.boolean(),
  round: z.number().int(),
});

export const AmendmentView = z.object({
  id: z.uuid(),
  number: z.number().int(),
  reasonKind: z.enum(Object.keys(AMENDMENT_REASONS) as [AmendmentReason, ...AmendmentReason[]]),
  reason: z.string(),
  oldAmount: Money.nullable(),
  newAmount: Money.nullable(),
  amountChanged: z.boolean(),
  oldEndDate: DateStr.nullable(),
  newEndDate: DateStr.nullable(),
  oldPaymentTerms: z.string().nullable(),
  newPaymentTerms: z.string().nullable(),
  oldCostArticle: z.string().nullable(),
  newCostArticle: z.string().nullable(),
  scopeNote: z.string().nullable(),
  securityCheck: z.boolean(),
  status: z.enum(Object.keys(AMENDMENT_STATUSES) as [AmendmentStatus, ...AmendmentStatus[]]),
  signedDate: DateStr.nullable(),
  createdByName: z.string().nullable(),
  /** Автор: пункты подразделений он не согласует (ДГ-04) */
  createdById: z.uuid().nullable(),
  createdAt: z.string(),
  /** Редакция допсоглашения: подписывается та, которую подписант видел */
  version: z.string(),
  /** Меняются сумма, цены, графики или порядок оплаты: решают те, кто видит суммы (ДС-04) */
  moneyChange: z.boolean(),
  cancelReason: z.string().nullable(),
  /** Запрос на изменение, по которому оформлено допсоглашение: номер и ссылка (суммы запроса — на его странице) */
  change: z.object({ id: z.uuid(), number: z.string().nullable() }).nullable(),
  /** Пункты маршрута текущего раунда */
  approvals: z.array(ApprovalView),
  /** Сравнение условий «было → стало» */
  changes: z.array(ChangeLine),
  /**
   * ДГ-04: на повторном согласовании — что изменилось с прошлого раунда; в проекте после возврата —
   * правки с последней отправки
   */
  roundChanges: z.array(ChangeLine),
  /** Новая спецификация и графики для правки проекта допсоглашения; цены — тем, кто видит суммы */
  items: z
    .array(
      ContractItemInput.extend({
        price: Money.nullable(),
      }),
    )
    .nullable(),
  schedule: z.array(ScheduleRowInput.extend({ amount: Money.nullable().optional() })).nullable(),
});
export type AmendmentView = z.infer<typeof AmendmentView>;

export const ContractDetail = ContractSummary.extend({
  legalEntityId: z.uuid(),
  legalEntityName: z.string(),
  vatRate: z.number(),
  startDate: DateStr.nullable(),
  costArticle: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  selectionReason: z.string().nullable(),
  securityMemo: z.string().nullable(),
  cardConfirmedAt: z.string().nullable(),
  cardConfirmedBy: z.string().nullable(),
  oneCNumber: z.string().nullable(),
  oneCRegisteredAt: DateStr.nullable(),
  note: z.string().nullable(),
  items: z.array(
    ContractItemInput.extend({
      id: z.uuid(),
      amount: Money.nullable(),
      estimatePrice: Money.nullable(),
    }),
  ),
  schedule: z.array(
    ScheduleRowInput.extend({
      id: z.uuid(),
      /** Заявки на оплату по плановому платежу (кроме отменённых) и оплаченное по ним (ФН-02) */
      requested: Money.nullable(),
      paid: Money.nullable(),
    }),
  ),
  advanceGuarantee: z.boolean(),
  penaltyRate: z.number().nullable(),
  penaltyCap: z.number().nullable(),
  /** Почему требование гарантии изменено после подписания — решение финансового менеджера (ФН-19) */
  advanceGuaranteeNote: z.string().nullable(),
  approvals: z.array(
    z.object({
      id: z.uuid(),
      step: z.enum(Object.keys(APPROVAL_STEPS) as [ApprovalStep, ...ApprovalStep[]]),
      status: z.enum(['pending', 'approved', 'rejected', 'returned']),
      approverName: z.string().nullable(),
      /** Кто решил пункт: в раунде один человек решает один пункт (ДГ-04) */
      decidedById: z.uuid().nullable(),
      comment: z.string().nullable(),
      dueAt: DateStr.nullable(),
      decidedAt: z.string().nullable(),
      overdue: z.boolean(),
      round: z.number().int(),
    }),
  ),
  /** Сумма спецификации и плана платежей — для проверки до подписания */
  itemsTotal: Money.nullable(),
  paymentsTotal: Money.nullable(),
  /** Возможное дробление закупки (ЗК-33) — то же, что в карточке заявки; null — не найдено */
  split: SplitView.nullable(),
  warnings: z.array(z.string()),
  /** Допсоглашения и история версий договора (ДГ-08) */
  amendments: z.array(AmendmentView),
  /** ДГ-04: что изменилось в проекте договора с прошлой отправки на согласование */
  versionChanges: z.array(ChangeLine),
  /** Редакция договора: подписывается та, которую подписант видел */
  version: z.string(),
  /** Автор проекта: пункты подразделений он не согласует (ДГ-04) */
  createdById: z.uuid().nullable(),
});
export type ContractDetail = z.infer<typeof ContractDetail>;

export const DecisionInput = z.object({
  decision: z.enum(['approved', 'rejected', 'returned']),
  comment: z.string().trim().max(2000).optional(),
});
export type DecisionInput = z.infer<typeof DecisionInput>;

export const SignInput = z.object({
  signedDate: DateStr,
  /** Подписать, хотя маршрут не завершён — договор будет отмечен (ДГ-16) */
  bypass: z.boolean().optional(),
  /** Редакция, которую видел подписант (ContractDetail.version): подписывается только она */
  version: z.string().max(40),
});
export type SignInput = z.infer<typeof SignInput>;

export const AmendmentSignInput = z.object({
  signedDate: DateStr,
  /** Редакция допсоглашения, которую видел подписант (AmendmentView.version) */
  version: z.string().max(40),
});
export type AmendmentSignInput = z.infer<typeof AmendmentSignInput>;

export const AmendmentCancelInput = z.object({
  /** Причина обязательна, если допсоглашение уже на согласовании или согласовано */
  reason: z.string().trim().max(2000).optional(),
});
export type AmendmentCancelInput = z.infer<typeof AmendmentCancelInput>;

/**
 * Исполнение расходного договора (ДГ-07, ДГ-13): поставлено и оплачено против договора, остаток к поставке и к
 * оплате, просроченные строки графика поставок и неустойка по условиям договора. Суммы — по уровню денег (null).
 */
export const ContractExecution = z.object({
  contractId: z.uuid(),
  /** Валюта договора: суммы ниже — в ней */
  currency: z.string(),
  amount: Money.nullable(),
  delivered: Money.nullable(),
  toDeliver: Money.nullable(),
  paid: Money.nullable(),
  toPay: Money.nullable(),
  /** Оплачено сверх суммы договора */
  overpaid: Money.nullable(),
  /** Позиции спецификации: по договору, получено на склад, осталось */
  items: z.array(
    z.object({
      name: z.string(),
      unit: z.string(),
      contracted: z.number(),
      received: z.number(),
      remaining: z.number(),
    }),
  ),
  /** Строки графика поставок, срок которых прошёл, а поставка не закрыта */
  overdue: z.array(
    z.object({
      rowId: z.uuid(),
      description: z.string().nullable(),
      plannedDate: z.string(),
      days: z.number().int(),
      /** Стоимость непоставленного по строке; без позиции у строки — оценка по доле суммы договора */
      value: Money.nullable(),
      estimated: z.boolean(),
      penalty: Money.nullable(),
    }),
  ),
  penaltyRate: z.number().nullable(),
  penaltyCap: z.number().nullable(),
  /** Неустойка с учётом предела */
  penalty: Money.nullable(),
  penaltyCapped: z.boolean(),
});
export type ContractExecution = z.infer<typeof ContractExecution>;
